import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import LinearGradient from 'react-native-linear-gradient';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import FleetMetricsService from '../services/fleetMetricsService';
import DepositService from '../services/depositService';
import { ErrorLogger } from '../utils/errorLogger';
import { formatAmount as fmt } from '../utils/formatAmount';

interface WeeklyStats {
  totalIncome: number;
  totalExpenses: number;
  totalJobs: number;
  cashJobs: number;
  debtJobs: number;
  cashAtHand: number;
  moneyOwed: number;
  incomeChange: number;
  expensesChange: number;
  totalDeposited: number;
  pendingDeposits: number;
}


const RevenueSummaryCard: React.FC = () => {
  const [stats, setStats] = useState<WeeklyStats | null>(null);
  const [loading, setLoading] = useState(true);

  const loadWeeklyStatistics = useCallback(async () => {
    try {
      setLoading(true);
      const [weeklyStats, depositStats] = await Promise.all([
        FleetMetricsService.getWeeklySnapshot(),
        DepositService.getDepositStatistics(),
      ]);
      setStats({
        ...weeklyStats,
        totalDeposited: depositStats.completedAmount,
        pendingDeposits: depositStats.pendingAmount,
      });
    } catch (error) {
      ErrorLogger.logError(error as any, 'RevenueSummaryCard.loadWeeklyStatistics');
      setStats({
        totalIncome: 0, totalExpenses: 0, totalJobs: 0, cashJobs: 0,
        debtJobs: 0, cashAtHand: 0, moneyOwed: 0, incomeChange: 0,
        expensesChange: 0, totalDeposited: 0, pendingDeposits: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWeeklyStatistics(); }, [loadWeeklyStatistics]);

  const trendPositive = (stats?.incomeChange ?? 0) >= 0;
  const trendLabel = stats
    ? `${trendPositive ? '+' : ''}${stats.incomeChange.toFixed(1)}% vs last week`
    : '';

  return (
    <View style={styles.wrapper}>
      {/* Single unified gradient card containing hero + metric tiles */}
      <LinearGradient
        colors={['#063669', '#274e82']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.heroCard}
      >
        {loading || !stats ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="white" />
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        ) : (
          <>
            {/* Hero section */}
            <Text style={styles.heroLabel}>Total Earnings</Text>
            <Text style={styles.heroAmount}>{fmt(stats.totalIncome)}</Text>
            <Text style={styles.heroTrips}>
              {stats.totalJobs} job{stats.totalJobs !== 1 ? 's' : ''}
            </Text>
            <View style={[
              styles.trendBadge,
              { backgroundColor: trendPositive ? 'rgba(255,255,255,0.18)' : 'rgba(255,181,154,0.25)' },
            ]}>
              <Text style={styles.trendText}>
                {trendPositive ? '↗' : '↘'} {trendLabel}
              </Text>
            </View>

            {/* Divider */}
            <View style={styles.divider} />

            {/* Metric tiles — same card, same theme */}
            <View style={styles.metricGrid}>
              <MetricTile
                label="Cash at Hand"
                subLabel="Liquid Assets"
                value={fmt(stats.cashAtHand)}
                icon="cash"
              />
              <MetricTile
                label="Deposited"
                subLabel="Completed deposits"
                value={fmt(stats.totalDeposited)}
                icon="bank"
              />
            </View>
          </>
        )}
      </LinearGradient>

      {/* Pending deposits warning — outside the card */}
      {stats && !loading && stats.pendingDeposits > 0 && (
        <View style={styles.pendingStrip}>
          <MaterialCommunityIcons name="alert-circle-outline" size={16} color="#380d00" />
          <Text style={styles.pendingText}>
            {'  '}Pending Deposits · {fmt(stats.pendingDeposits)}
          </Text>
        </View>
      )}
    </View>
  );
};

interface MetricTileProps {
  label: string;
  subLabel?: string;
  value: string;
  icon: string;
}

const MetricTile: React.FC<MetricTileProps> = ({ label, subLabel, value, icon }) => (
  <View style={styles.metricTile}>
    <View style={styles.metricHeader}>
      <Text style={styles.metricLabel}>{label}</Text>
      <MaterialCommunityIcons name={icon} size={20} color="rgba(255,255,255,0.6)" />
    </View>
    <Text style={styles.metricValue}>{value}</Text>
    {subLabel && (
      <Text style={styles.metricSubLabel}>{subLabel}</Text>
    )}
  </View>
);

const styles = StyleSheet.create({
  wrapper: {
    paddingHorizontal: 20,
  },
  heroCard: {
    borderRadius: 6,
    padding: 20,
    marginBottom: 12,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    elevation: 4,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    gap: 10,
  },
  loadingText: {
    fontFamily: 'Inter-Regular',
    color: 'white',
    fontSize: 14,
  },
  heroLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  heroAmount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 34,
    color: 'white',
    marginBottom: 4,
  },
  heroTrips: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: 'rgba(255,255,255,0.65)',
    marginBottom: 14,
  },
  trendBadge: {
    alignSelf: 'flex-start',
    borderRadius: 9999,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  trendText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: 'rgba(255,255,255,0.9)',
  },
  divider: {
    borderTopWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    marginVertical: 16,
  },
  metricGrid: {
    flexDirection: 'row',
    gap: 10,
  },
  metricTile: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 6,
    padding: 14,
  },
  metricHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  metricLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: 'rgba(255,255,255,0.9)',
    flex: 1,
    marginRight: 8,
  },
  metricValue: {
    fontFamily: 'Manrope-Bold',
    fontSize: 18,
    color: 'white',
    marginBottom: 4,
  },
  metricSubLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.55)',
  },
  pendingStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffb59a',
    borderRadius: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginBottom: 4,
  },
  pendingText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: '#380d00',
    flex: 1,
  },
});

export default RevenueSummaryCard;
